import Contributor from "@/models/SvcModels/Contributor";
import Role from "@/models/SvcModels/Role";
import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import { useMissionStore } from "@/store/modules/mission";
import { contributorsConfig } from "@/setup/openApiConfig";
import { ContributorApi, ProjectContributorApi } from "@socotec.io/contributors-sdk";

import { rolesNames } from "@/constants/contributors/roles.js";
import { cleanDatetimes } from "@/utils/dataShape";
import _ from "lodash";
const excludeFields = [
  "metadata",
  "createdAt",
  "updatedAt",
  "project",
  "role",
  "batches",
  "company",
  "isLocked",
];

const sanitizeResponse = (response) => {
  const rest = _.omit(response, ["companyProjectRole", "role"]);
  const { companyProjectRole } = response;
  if (!companyProjectRole) return rest;
  return { ...rest, companyProjectRole: _.omit(companyProjectRole, ["contributors"]) };
};

const ContributorRepo = useRepo(Contributor);
const RoleRepo = useRepo(Role);

const contributorApi = new ContributorApi(contributorsConfig);
const projectContributorApi = new ProjectContributorApi(contributorsConfig);

export const useContributorStore = defineStore("contributor", {
  state: () => ({
    contributorsCount: 0,
    contributorsTotal: 0,
  }),

  actions: {
    async fetchContributors({ projectUuid, ...params } = {}) {
      ContributorRepo.flush();
      const missionsStore = useMissionStore();
      const response = await projectContributorApi.list({
        projectUuid: projectUuid || missionsStore.currentMissionUuid,
        ...params,
      });
      this.updateContributorsCount(response.count);
      return ContributorRepo.save(response.results.map(sanitizeResponse));
    },

    async createContributor(
      contributor,
      { projectUuid = useMissionStore().currentMissionUuid } = {},
    ) {
      cleanDatetimes(contributor, ["arrivalDate", "plannedEndDate"]);
      const response = await projectContributorApi.create({
        projectUuid,
        contributorInputRequest: _.omit(contributor, excludeFields),
      });

      this.addContributorsCount(1);

      const dataInserted = ContributorRepo.save(sanitizeResponse(response));
      return dataInserted.uuid;
    },

    async updateContributor(
      contributor,
      { projectUuid = useMissionStore().currentMissionUuid } = {},
    ) {
      cleanDatetimes(contributor, ["arrivalDate", "plannedEndDate"]);
      const response = await projectContributorApi.partialUpdate({
        projectUuid,
        uuid: contributor.uuid,
        patchedContributorInputRequest: _.omit(contributor, excludeFields),
      });

      const dataInserted = ContributorRepo.save(sanitizeResponse(response));
      return dataInserted.uuid;
    },

    async deleteContributor(
      contributor,
      { projectUuid = useMissionStore().currentMissionUuid } = {},
    ) {
      await projectContributorApi.destroy({
        projectUuid,
        uuid: contributor.uuid,
      });

      this.addContributorsCount(-1);

      ContributorRepo.destroy(contributor.uuid);
    },

    async searchContributor(filters) {
      const response = await contributorApi.list(filters);
      return response.results.map((contributor) => new Contributor(contributor));
    },

    updateContributorsTotal(total) {
      this.contributorsTotal = total;
    },

    updateContributorsCount(newTotal) {
      this.contributorsCount = newTotal;
    },

    addContributorsCount(number) {
      this.contributorsCount += number;
      this.contributorsTotal += number;
    },
  },

  getters: {
    getRoleByName: () => (roleName) => {
      return RoleRepo.where("name", roleName).first();
    },

    isMissionHasUserWithRole: () => (role) => {
      const missionsStore = useMissionStore();
      return (
        ContributorRepo.whereHas("companyProjectRole", (query) => {
          query
            .where("projectId", missionsStore.currentMission.uuid)
            .whereHas("role", (queryRole) => {
              queryRole.where("name", role);
            });
        }).get().length > 0
      );
    },

    getMissionSupervisor: () => {
      const missionsStore = useMissionStore();
      return ContributorRepo.whereHas("companyProjectRole", (query) => {
        query
          .where("projectId", missionsStore.currentMission.uuid)
          .whereHas("role", (queryRole) => {
            queryRole.where("name", rolesNames.SUPERVISOR);
          });
      }).first();
    },

    getMissionProjectManager: () => {
      const missionsStore = useMissionStore();
      return ContributorRepo.whereHas("companyProjectRole", (query) => {
        query
          .where("projectId", missionsStore.currentMission.uuid)
          .whereHas("role", (queryRole) => {
            queryRole.where("name", rolesNames.PROJECT_MANAGER);
          });
      }).first();
    },

    getMissionPilot: () => {
      const missionsStore = useMissionStore();
      return ContributorRepo.whereHas("companyProjectRole", (query) => {
        query.where("projectId", missionsStore.currentMission.uuid).whereHas("role", (query) => {
          query.where("name", rolesNames.PROJECT_MANAGER);
        });
      }).first();
    },

    getMissionProjectLead: () => {
      const missionsStore = useMissionStore();
      return ContributorRepo.whereHas("companyProjectRole", (query) => {
        query.where("projectId", missionsStore.currentMission.uuid).whereHas("role", (query) => {
          query.where("name", rolesNames.PROJECT_LEAD);
        });
      }).first();
    },

    getContributorByUuid: () => (uuid) => {
      const contributor = ContributorRepo.where("uuid", uuid)
        .with("companyProjectRole", (query) => query.with("role").with("company"))
        .first();
      return {
        ...contributor,
        role: contributor?.companyProjectRole?.role,
        company: contributor?.companyProjectRole?.company,
      };
    },

    getContributorsCount: (state) => state.contributorsCount,
    getContributorsTotal: (state) => state.contributorsTotal,

    getMissionContributors: () => {
      const missionsStore = useMissionStore();
      return ContributorRepo.whereHas("companyProjectRole", (query) => {
        query.where("projectId", missionsStore.currentMission.uuid);
      })
        .with("companyProjectRole", (query) => query.with("role").with("company"))
        .orderBy("userLastName")
        .get()
        .map((contributor) => {
          if (contributor.companyProjectRole?.role?.name === "PROJECT_MANAGER") {
            contributor.companyProjectRole.role.name = "PILOT";
          }
          return {
            ...contributor,
            role: contributor.companyProjectRole?.role,
            company: contributor.companyProjectRole?.company,
          };
        });
    },
  },
});
